/**
 * ops 新規依頼フォームの下書きを localStorage に保存 / 復元 / 破棄する（サーバー非依存・per-browser）。
 * 中身があるあいだだけ保存し、フォーム復帰時に自動で戻し、送信成功で破棄する。Firestore には依存しない。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 新規依頼フォームの下書きをブラウザ localStorage に保存・復元・破棄する。中身がある時だけ保存し、フォーム復帰で自動復元、送信成功で破棄する軽量下書き（サーバー非依存）
 * @graph-connects none
 */
import { logError } from '@cortex/faro';
import type { OpsCategory, OpsService, OpsUrgency } from '../types/ops.js';

/** localStorage のキー（形が変わったら vN を上げて旧下書きを無効化する）。 @graph-connects none */
const DRAFT_KEY = 'ops-submit-draft-v1';

/** 保存する下書きの形（フォーム入力そのまま + 保存時刻）。 @graph-connects none */
export interface OpsSubmitDraft {
  /** 対象サービス。 */
  service: OpsService;
  /** カテゴリ。 */
  category: OpsCategory;
  /** 対象ユーザー ID（入力欄の文字列）。 */
  userId: string;
  /** ユーザー非依存（N/A）か。 */
  userNa: boolean;
  /** 緊急度。 */
  urgency: OpsUrgency;
  /** 背景（5W）。 */
  background: string;
  /** 対応してほしい内容。 */
  requested: string;
  /** WF 申請 ID。 */
  wfid: string;
  /** CS チケット URL。 */
  csUrl: string;
  /** 保存時刻（epoch ms）。 */
  savedAt: number;
}

/** 下書きに実質的な入力があるか（空フォームを下書き扱いしない）。 @graph-connects none */
export function draftHasContent(draft: OpsSubmitDraft): boolean {
  return (
    draft.userId.trim() !== '' ||
    draft.background.trim() !== '' ||
    draft.requested.trim() !== '' ||
    draft.wfid.trim() !== '' ||
    draft.csUrl.trim() !== ''
  );
}

/** 復元前の最低限の形チェック（壊れた / 旧バージョンの値を復元しない）。 @graph-connects none */
function isOpsSubmitDraft(value: unknown): value is OpsSubmitDraft {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.background === 'string' &&
    typeof v.requested === 'string' &&
    typeof v.userId === 'string' &&
    typeof v.savedAt === 'number'
  );
}

/** 下書きを localStorage から読む（不在・壊れた値は null）。 @graph-connects none */
export function loadOpsDraft(): OpsSubmitDraft | null {
  const raw = localStorage.getItem(DRAFT_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isOpsSubmitDraft(parsed) ? parsed : null;
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'ops_draft_parse_failed',
    });
    return null;
  }
}

/** 下書きを保存する。 @graph-connects none */
export function saveOpsDraft(draft: OpsSubmitDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {
    // localStorage が使えない環境（Safari プライベート等）は下書き保存を諦める（機能低下は許容）。
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'ops_draft_save_failed',
    });
  }
}

/** 下書きを破棄する。 @graph-connects none */
export function clearOpsDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}
